import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { FiUsers, FiClock, FiLayers } from 'react-icons/fi';

const UsersList = () => {
  const { user: currentUser } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchList = async () => {
      try {
        const res = await axios.get('/users/list');
        setList(res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load list');
      } finally {
        setLoading(false);
      }
    };
    fetchList();
  }, []);

  const isAdmin = currentUser?.role === 'admin';
  const title = isAdmin ? 'Users' : 'Team';
  const subtitle = isAdmin ? 'All users with hours & tasks' : 'Team members with hours & tasks';

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-300" />
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
          <p className="text-dark-100 text-sm">{subtitle}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {list.length === 0 ? (
          <div className="bg-stroke border border-stroke rounded-lg p-12 text-center">
            <FiUsers className="text-6xl text-dark-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No users</h3>
            <p className="text-dark-100">{isAdmin ? 'No users in the system.' : 'No team members in your teams.'}</p>
          </div>
        ) : (
          <div className="bg-stroke border border-stroke rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="text-left border-b border-stroke">
                    <th className="py-3 px-4 text-dark-100 font-medium">User</th>
                    <th className="py-3 px-4 text-dark-100 font-medium">Role</th>
                    <th className="py-3 px-4 text-dark-100 font-medium flex items-center gap-1">
                      <FiClock /> Hours
                    </th>
                    <th className="py-3 px-4 text-dark-100 font-medium">Overtime</th>
                    <th className="py-3 px-4 text-dark-100 font-medium flex items-center gap-1">
                      <FiLayers /> Tasks
                    </th>
                    <th className="py-3 px-4 text-dark-100 font-medium">Done</th>
                    <th className="py-3 px-4 text-dark-100 font-medium">In progress</th>
                    <th className="py-3 px-4 text-dark-100 font-medium">Pending</th>
                    <th className="py-3 px-4 text-dark-100 font-medium">Avg %</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => (
                    <tr key={row._id} className="border-b border-stroke hover:bg-white/5">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-300 flex items-center justify-center text-white font-semibold shrink-0">
                            {row.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-white font-medium">{row.name}</div>
                            <div className="text-dark-100 text-sm">{row.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-dark-100 text-xs bg-dark-300 px-2 py-1 rounded">
                          {row.role}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-white font-medium">{row.totalHours}h</td>
                      <td className="py-4 px-4 text-amber-400">{row.totalOvertime}h</td>
                      <td className="py-4 px-4 text-white">{row.totalTasks}</td>
                      <td className="py-4 px-4 text-green-400">{row.completedTasks}</td>
                      <td className="py-4 px-4 text-blue-400">{row.inProgressTasks}</td>
                      <td className="py-4 px-4 text-yellow-400">{row.pendingTasks}</td>
                      <td className="py-4 px-4">
                        <span className="text-white font-semibold">{row.averageProgress}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersList;
